import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      if (honeypotField) {
        formData.delete('phone_alt');
      }

      const response = await fetch('https://readdy.ai/api/form/d9qqkcue3oq4jqumtp1g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  const milestones = [
    { year: '2024', title: '平台正式上线', desc: '国产第一页正式发布，首批收录200+中国自主品牌' },
    { year: '2025', title: '品牌突破500+', desc: '平台收录品牌超过500个，月活用户突破50万' },
    { year: '2025', title: '全面升级', desc: '新增文章深度解读、品牌分类导航等核心功能' },
    { year: '2026', title: '迈向百万用户', desc: '月活用户突破100万，成为中国最权威的国产品牌导航平台' },
  ];

  const values = [
    {
      icon: 'ri-shield-check-line',
      title: '权威可信',
      desc: '所有收录品牌均经过专业团队严格审核，确保信息真实可靠，为用户提供最权威的品牌参考。',
    },
    {
      icon: 'ri-eye-line',
      title: '客观公正',
      desc: '坚持第三方独立视角，不偏袒任何品牌，以真实数据和用户反馈为基础进行客观评价。',
    },
    {
      icon: 'ri-lightbulb-flash-line',
      title: '助力国货',
      desc: '以推动中国自主品牌发展为己任，让更多优秀国货品牌被看到、被认可、被选择。',
    },
    {
      icon: 'ri-heart-line',
      title: '用户至上',
      desc: '始终以用户需求为核心，持续优化体验，做消费者最信赖的国货品牌导航工具。',
    },
  ];

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '国产第一页',
            url: 'https://guochandiyiye.com/about',
            description: '中国自主品牌权威导航与推荐平台',
            foundingDate: '2024',
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: '400-888-9999',
              contactType: 'customer service',
              email: 'contact@guochandiyiye.com',
            },
            address: {
              '@type': 'PostalAddress',
              addressLocality: '北京',
              addressRegion: '北京市',
              addressCountry: 'CN',
              streetAddress: '朝阳区建国路88号',
            },
          }),
        }}
      />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        <section className="relative w-full h-[350px] md:h-[450px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Chinese%20brand%20headquarters%20building%20with%20modern%20architecture%2C%20warm%20golden%20hour%20lighting%2C%20professional%20corporate%20photography%2C%20glass%20facade%20reflection%2C%20grand%20atmosphere&width=1920&height=900&seq=about-hero-01&orientation=landscape"
              alt="关于国产第一页"
              title="国产第一页 - 关于我们"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-4">
              关于我们
            </h1>
            <p className="text-sm md:text-lg text-background-200 max-w-xl mx-auto">
              让每一位用户在国产第一页找到值得信赖的中国制造
            </p>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
              品牌故事
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-6">
              汇聚国产精品，助力国货崛起
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
              国产第一页诞生于中国品牌崛起的浪潮之中。我们看到越来越多的中国自主品牌正在以卓越的品质、创新的技术和独特的文化魅力赢得全球消费者的认可。然而，在信息爆炸的时代，消费者往往难以在海量信息中快速找到值得信赖的国产品牌。
            </p>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
              我们致力于打造中国最权威的国产品牌导航与推荐平台。通过专业的品牌筛选机制、客观的多维度评价体系以及深度的行业解读内容，国产第一页为消费者提供一站式国货品牌发现体验。从科技数码到美妆护肤，从新能源汽车到文创潮玩，我们的品牌库覆盖中国自主品牌全领域。
            </p>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              国产第一页不仅是一个品牌导航工具，更是一个助力国货崛起的平台。我们希望通过自己的努力，让更多优秀的中国品牌被看到、被认可、被选择。让每一位消费者都能在这里找到值得信赖的中国制造，让世界爱上中国品牌。
            </p>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                核心价值
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
                我们的承诺
              </h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                四大核心价值观，指导我们持续为用户创造价值
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {values.map((val) => (
                <div
                  key={val.title}
                  className="bg-background-50 rounded-lg p-6 text-center"
                >
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                    <i className={`${val.icon} text-primary-500 text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">
                    {val.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {val.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
                发展历程
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
                我们的成长之路
              </h2>
            </div>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200 md:-translate-x-px"></div>
              <div className="space-y-8">
                {milestones.map((m, idx) => (
                  <div
                    key={m.year}
                    className={`relative flex items-start gap-6 ${
                      idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="hidden md:block flex-1"></div>
                    <div className="relative z-10 w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 shrink-0">
                      <div className="w-3 h-3 rounded-full bg-background-50"></div>
                    </div>
                    <div className="flex-1 bg-background-100 rounded-lg p-5">
                      <span className="text-xs text-primary-500 font-bold">{m.year}</span>
                      <h3 className="font-heading text-base font-bold text-foreground-950 mt-1 mb-1">
                        {m.title}
                      </h3>
                      <p className="text-sm text-foreground-500">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                联系我们
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
                品牌入驻与合作
              </h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                欢迎优秀国产品牌与我们联系，共同助力国货崛起
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 shrink-0">
                    <i className="ri-mail-line text-primary-500 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-heading text-base font-bold text-foreground-950 mb-1">电子邮箱</h4>
                    <p className="text-sm text-foreground-500">contact@guochandiyiye.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 shrink-0">
                    <i className="ri-phone-line text-primary-500 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-heading text-base font-bold text-foreground-950 mb-1">联系电话</h4>
                    <p className="text-sm text-foreground-500">400-888-9999</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 shrink-0">
                    <i className="ri-map-pin-line text-primary-500 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-heading text-base font-bold text-foreground-950 mb-1">公司地址</h4>
                    <p className="text-sm text-foreground-500">北京市朝阳区建国路88号</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 shrink-0">
                    <i className="ri-time-line text-primary-500 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-heading text-base font-bold text-foreground-950 mb-1">工作时间</h4>
                    <p className="text-sm text-foreground-500">周一至周五 9:00 - 18:00</p>
                  </div>
                </div>
              </div>

              <div>
                <form
                  id="about-contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="bg-background-50 rounded-lg p-6 space-y-4"
                >
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">
                      姓名
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-3 py-2.5 text-sm bg-background-100 border border-background-200 rounded-md text-foreground-950 focus:outline-none focus:border-primary-500 transition-colors duration-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-3 py-2.5 text-sm bg-background-100 border border-background-200 rounded-md text-foreground-950 focus:outline-none focus:border-primary-500 transition-colors duration-200"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-company" className="block text-sm font-medium text-foreground-700 mb-1">
                      品牌/公司名称
                    </label>
                    <input
                      id="contact-company"
                      name="company"
                      type="text"
                      required
                      className="w-full px-3 py-2.5 text-sm bg-background-100 border border-background-200 rounded-md text-foreground-950 focus:outline-none focus:border-primary-500 transition-colors duration-200"
                      placeholder="请输入品牌或公司名称"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">
                      留言内容
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2.5 text-sm bg-background-100 border border-background-200 rounded-md text-foreground-950 focus:outline-none focus:border-primary-500 transition-colors duration-200 resize-none"
                      placeholder="请简要描述您的品牌及入驻意向（500字以内）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="antispam-field"
                  />
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 cursor-pointer disabled:opacity-60 whitespace-nowrap"
                  >
                    {formStatus === 'loading' ? '提交中...' : '提交入驻申请'}
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-center text-green-600 font-medium">
                      提交成功！我们将在5个工作日内与您联系
                    </p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-center text-red-500 font-medium">
                      {formError}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}